import { computed, inject } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { WINDOW } from '@tvd/cdk/browser';
import { signalStore } from '@tvd/cdk/store';
import { EMPTY, catchError, finalize, tap } from 'rxjs';
import { AuthService } from '../auth';
import {
  type CreateCheckoutSessionRequest,
  type CreatePortalSessionRequest,
  type PaymentRequirement,
  SubscriptionApi,
  type SubscriptionStatus,
} from './subscription-api';

const initState = {
  loading: false,
  creating: false,
  status: null as SubscriptionStatus | null,
  paymentRequirement: null as PaymentRequirement | null,
};

export const SubscriptionStore = signalStore(initState).create(
  (state, patch) => {
    const subscriptionApi = inject(SubscriptionApi);
    const winRef = inject(WINDOW);
    const auth = inject(AuthService);

    const statusResource = rxResource({
      params: auth.user.value,
      stream: ({ params: user }) => {
        if (!user) {
          return EMPTY;
        }
        patch({ loading: true });
        return subscriptionApi.getStatus().pipe(
          tap({
            next: (status) => patch({ status }),
            finalize: () => patch({ loading: false }),
          }),
        );
      },
    });

    return {
      // State
      loading: computed(() => state().loading),
      creating: computed(() => state().creating),
      status: computed(() => state().status),
      paymentRequirement: computed(() => state().paymentRequirement),

      // Data
      canCreateOrg: computed(() => state().status?.canCreateOrg ?? false),
      hasActiveSubscription: computed(
        () => state().status?.subscriptionActive ?? false,
      ),
      isScheduledForCancellation: computed(
        () => state().status?.subscriptionCancelledAtPeriodEnd ?? false,
      ),
      subscriptionEndDate: computed(
        () => state().status?.subscriptionCurrentPeriodEnd ?? null,
      ),
      subscriptionPlan: computed(
        () => state().status?.subscriptionPlan ?? null,
      ),
      subscriptionPlanType: computed(
        () => state().status?.subscriptionPlanType ?? null,
      ),
      subscriptionPlanTier: computed(
        () => state().status?.subscriptionPlanTier ?? null,
      ),
      subscriptionAmount: computed(
        () => state().status?.subscriptionAmount ?? null,
      ),
      subscriptionCurrency: computed(
        () => state().status?.subscriptionCurrency ?? null,
      ),
      // SUB-004 — itemized breakdown (base plan + AI+/Flow add-ons). The
      // billing card shows it beneath the full total when add-ons are present.
      subscriptionLineItems: computed(
        () => state().status?.subscriptionLineItems ?? [],
      ),
      subscriptionFeatureTier: computed(
        () => state().status?.subscriptionFeatureTier ?? null,
      ),
      // SUB-004 — POS is unlocked iff active && flowUnits >= 1 (server-driven).
      hasPos: computed(() => state().status?.hasPos ?? false),

      // SUB-004 — the owner's POS Kasse entitlement = number of Flow units
      // (null until status loads). Owner-facing billing display; the
      // provisioning gate uses the org-scoped quota from the cash-registers org
      // status endpoint.
      tillLimit: computed(() => state().status?.tillLimit ?? null),
      // SUB-004 — number of Flow add-on units (alias of tillLimit).
      flowUnits: computed(
        () => state().status?.flowUnits ?? state().status?.tillLimit ?? 0,
      ),
      // SUB-004 — number of AI+ add-on units (each = 1,000 tokens/month).
      aiUnits: computed(() => state().status?.aiUnits ?? 0),
      // SUB-004 — true iff at least one AI+ unit is held.
      withAi: computed(
        () => state().status?.withAi ?? (state().status?.aiUnits ?? 0) >= 1,
      ),
      // SUB-006 — the busiest owned org's active-Kasse count; the lowest value
      // the Flow stepper may be reduced to (decommission tills to go below it).
      activeTillCount: computed(() => state().status?.activeTillCount ?? 0),

      // Trial information
      trialEndsAt: computed(() => state().status?.trialEndsAt ?? null),
      trialExpired: computed(() => state().status?.trialExpired ?? false),
      trialDaysLeft: computed(() => {
        const endsAtIso = state().status?.trialEndsAt;
        if (!endsAtIso) return 0;
        const ends = new Date(endsAtIso).getTime();
        const now = Date.now();
        if (Number.isNaN(ends) || ends <= now) return 0;
        const msLeft = ends - now;
        return Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
      }),

      // Payment requirement information
      needsPayment: computed(
        () => state().paymentRequirement?.needsPayment ?? false,
      ),
      paymentReason: computed(
        () => state().paymentRequirement?.reason ?? 'No payment required',
      ),

      // Actions
      refreshStatus: () => statusResource.reload(),

      // Check payment requirement proactively
      checkPaymentRequirement: () => {
        patch({ loading: true });
        return subscriptionApi.checkPaymentRequirement().pipe(
          tap((paymentRequirement) => {
            patch({ paymentRequirement, loading: false });
          }),
          catchError((error) => {
            patch({ loading: false });
            throw error; // Re-throw to maintain error handling
          }),
        );
      },

      // SUB-004 — change the Flow add-on quantity on the existing subscription,
      // then refresh status so the new entitlement (till_limit, hasPos) is
      // reflected.
      changeFlow: (quantity: number) => {
        patch({ creating: true });
        return subscriptionApi.changeFlow(quantity).pipe(
          tap(() => statusResource.reload()),
          finalize(() => patch({ creating: false })),
        );
      },

      // SUB-004 — change the AI+ add-on quantity on the existing subscription,
      // then refresh status so the new token allocation / with_ai is reflected.
      changeAiAddon: (quantity: number) => {
        patch({ creating: true });
        return subscriptionApi.changeAiAddon(quantity).pipe(
          tap(() => statusResource.reload()),
          finalize(() => patch({ creating: false })),
        );
      },

      createCheckoutSession: (request: CreateCheckoutSessionRequest) => {
        patch({ creating: true });
        return subscriptionApi.createCheckoutSession(request).pipe(
          tap((response) => {
            // Redirect to Stripe checkout
            winRef.location.href = response.url;
          }),
          finalize(() => patch({ creating: false })),
        );
      },

      getDebugInfo: () => {
        patch({ loading: true });
        return subscriptionApi
          .getDebugInfo()
          .pipe(finalize(() => patch({ loading: false })));
      },

      cancelSubscription: () => {
        patch({ creating: true });
        return subscriptionApi.cancelSubscription().pipe(
          tap(() => {
            // Refresh status after cancellation
            patch({ loading: true });
            subscriptionApi.getStatus().subscribe({
              next: (status) => {
                patch({ status, loading: false });
              },
              error: (error) => {
                patch({ loading: false });
              },
            });
          }),
          finalize(() => patch({ creating: false })),
        );
      },

      createPortalSession: (request: CreatePortalSessionRequest) => {
        patch({ creating: true });
        return subscriptionApi.createPortalSession(request).pipe(
          tap((response) => {
            // Redirect to Stripe customer portal
            winRef.location.href = response.url;
          }),
          finalize(() => patch({ creating: false })),
        );
      },
    };
  },
);
